// Offset_Pure.ts — PURE polygon offset for the Buildings wall layer (§4.1).
//
// Set2 (§2) changed WHICH ring the room outline is: it is now the wall CENTERLINE, and the two
// wall faces are the centerline offset by ∓`t/2` (see `CenterlineToWallRings`). Set1 treated the
// outline as the INTERIOR face and grew the wall outward by the full `t`. The offset math itself is
// unchanged — only the distance + interpretation at the call sites. This reuses the same miter-join
// normal convention as `PathTransforms_Pure.ts`'s `ComputeNormal`/`ApplyExtrude`, but as a
// self-contained pure module (no three / mobx) so the Node vitest lane imports it directly.
//
// Convex corners miter with the exact `1/cos(θ/2)` scale so a rectangle offset by `t` grows each
// dimension by exactly `2t`; concave corners (and features narrower than ~2×distance) get the
// self-intersection collapse that bridges the pocket mouth. Documented limit (§4.1): an offset
// larger than half the smallest concave feature degenerates — the collapse mitigates it, but the
// caller should warn/clamp when `distance` is large relative to the footprint.
//
// OUTWARD normal: for a CCW polygon (interior on the left when traversing), the outward normal at
// each vertex is the miter of the RIGHT normals of its two incident edges — the same convention
// `ComputeWallSegments` (in `BuildingFootprint_Pure.ts`) uses for the wall outward normal, so the
// outer ring and the wall faces agree on direction.
import type {Vec2Simple} from "../../Utils/ThreeJS/VectorStructs.js";

export type Vec2 = {x: number, y: number};

/** The three concentric rings of a CENTERLINE-convention wall (Set2 §2): the authored `centerline`, plus the interior face (`−t/2`) and exterior face (`+t/2`). All three are CLOSED + CCW with the same vertex count/order, matching `WallSolidBuilderOpts`.
 *  Set1 read the outline as the INTERIOR face and grew the wall outward by the full `t`; Set2 makes it the centerline and spans `[−t/2, +t/2]`. */
export type WallRings = {
	centerline: Vec2Simple[];
	innerRing: Vec2Simple[];
	outerRing: Vec2Simple[];
};

/** Split a CENTERLINE outline into its inner/exterior face rings (Set2 §2). Returns null on a degenerate outline AND on a degenerate `t/2` inset (`OffsetOutward` can drop below 3 points, or produce an INVERTED ring for an over-narrow convex feature — the miter self-crosses and the collapse returns a same-size ring wound the other way, which a vertex-count check misses).
 *  The acceptance test is therefore AREA-based: the inset must keep the winding and strictly shrink, the outset must strictly grow. Both are rejections, never fallbacks — a silently zero/inverted-thickness wall is the repo's "silently-disabled guard" failure mode. */
export function CenterlineToWallRings(centerline: Vec2Simple[], thickness: number): WallRings|null {
	// Reuse the offset module's own closing-duplicate normalization: offset by 0 returns the
	// normalized ring unchanged (the miter at distance 0 is the vertex itself).
	const normalized = OffsetOutward(centerline, 0);
	if (normalized.length < 3) return null;
	const centerlineArea = SignedArea(normalized);
	if (Math.abs(centerlineArea) < 1e-12) return null; // zero-area outline → no meaningful inset

	const inset = OffsetOutward(normalized, -thickness / 2);
	const outset = OffsetOutward(normalized, +thickness / 2);
	if (inset.length < 3 || outset.length < 3) return null;

	// Degeneracy guard (see the doc comment): a valid inset keeps the winding and shrinks.
	const insetArea = SignedArea(inset);
	const outsetArea = SignedArea(outset);
	const ccw = centerlineArea > 0;
	if (ccw ? (insetArea <= 1e-12 || insetArea >= centerlineArea) : (insetArea >= -1e-12 || insetArea <= centerlineArea)) return null;
	if (ccw ? (outsetArea <= centerlineArea) : (outsetArea >= centerlineArea)) return null;

	// Close all three rings (trailing duplicate of the first point) so consumers can treat them
	// uniformly, and so the miter ring's index i still corresponds to the centerline's vertex i.
	return {
		centerline: CloseRing(normalized),
		innerRing: CloseRing(inset),
		outerRing: CloseRing(outset),
	};
}

/** Signed polygon area (shoelace; positive = CCW). Local copy so this module stays free of the triangulator; the degeneracy guard needs BOTH winding directions. */
function SignedArea(points: Vec2Simple[]): number {
	let area = 0;
	for (let i = 0; i < points.length; i++) {
		const [x1, y1] = points[i];
		const [x2, y2] = points[(i + 1) % points.length];
		area += x1 * y2 - x2 * y1;
	}
	return area / 2;
}

/** Append the first point so the ring is explicitly closed (no-op when already closed). */
export function CloseRing(points: Vec2Simple[]): Vec2Simple[] {
	if (points.length == 0) return [];
	const [x0, y0] = points[0];
	const [lx, ly] = points[points.length - 1];
	if (Math.abs(lx - x0) < 1e-12 && Math.abs(ly - y0) < 1e-12) return points.slice();
	return [...points, [x0, y0] as Vec2Simple];
}

/** The OUTWARD-offset distance from the CENTERLINE to each face for a wall of thickness `t`. Named so the Set2 pairing (`∓t/2`) is explicit at every call site instead of two magic `/2`s drifting apart. */
export function HalfThicknessOffset(thickness: number): number {
	return thickness / 2;
}

/** Miter-join normal at vertex i of a CLOSED polygon (wraps first↔last), using the RIGHT-normal
 *  convention (perpendicular of the edge direction, rotated right). For a CCW polygon this is the
 *  OUTWARD normal. The returned vector is UNIT. */
export function ComputeOutwardNormal(points: Vec2Simple[], i: number): Vec2 {
	const n = points.length;
	const prev = points[(i - 1 + n) % n];
	const next = points[(i + 1) % n];
	const dx1 = points[i][0] - prev[0];
	const dy1 = points[i][1] - prev[1];
	const dx2 = next[0] - points[i][0];
	const dy2 = next[1] - points[i][1];
	const len1 = Math.hypot(dx1, dy1) || 1e-9;
	const len2 = Math.hypot(dx2, dy2) || 1e-9;
	// Right normals of the two incident edges.
	const n1x = dy1 / len1, n1y = -dx1 / len1;
	const n2x = dy2 / len2, n2y = -dx2 / len2;
	let mx = n1x + n2x, my = n1y + n2y;
	const mlen = Math.hypot(mx, my);
	if (mlen < 1e-9) return {x: n1x, y: n1y}; // collinear → single-edge normal
	mx /= mlen; my /= mlen;
	// For a convex corner (acute external angle), the miter overshoot is 1/cos(θ/2) where θ is the
	// exterior angle between the two edge normals. `mx+my` magnitude already encodes it, but to
	// get the EXACT offset distance (a rectangle grows by exactly 2t), scale the unit direction by
	// 1/cos(θ/2) = 2/|n1+n2|.
	const cosHalf = mlen / 2; // cos(θ/2) = |n1+n2| / 2 (since |n1|=|n2|=1)
	if (cosHalf < 1e-6) return {x: n1x, y: n1y}; // nearly-180° interior → avoid huge scale
	const scale = 1 / cosHalf;
	return {x: mx * scale, y: my * scale};
}

/** Offset `points` (a CLOSED or open CCW footprint) by `distance` metres along its outward miter normal, returning the ring WITHOUT a trailing closing duplicate (close it via `CloseRing`). A NEGATIVE `distance` offsets INWARD (Set2's `−t/2` inner face); the miter scale is sign-independent. Self-intersections from miter overshoot are collapsed so the ring stays simple. */
export function OffsetOutward(points: Vec2Simple[], distance: number): Vec2Simple[] {
	// Drop a trailing duplicate of the first point so index wrapping is clean. Must drop a CLOSING
	// duplicate (== points[0]), not just consecutive ones — a surviving closing dup makes
	// `ComputeOutwardNormal` at index 0 see prev == self, shearing the miter ring (2026-08-29).
	const closed: Vec2Simple[] = [];
	for (let i = 0; i < points.length; i++) {
		const p = points[i];
		const last = closed.length ? closed[closed.length - 1] : null;
		if (last == null || Math.abs(p[0] - last[0]) > 1e-9 || Math.abs(p[1] - last[1]) > 1e-9) closed.push(p);
	}
	// Drop a trailing duplicate of the first point (a closing duplicate is NOT consecutive with its
	// predecessor, so the loop above keeps it; it must be removed for clean index wrapping).
	while (closed.length >= 2) {
		const [x0, y0] = closed[0];
		const [lx, ly] = closed[closed.length - 1];
		if (Math.abs(lx - x0) < 1e-9 && Math.abs(ly - y0) < 1e-9) closed.pop();
		else break;
	}
	if (closed.length < 3) return [];
	const n = closed.length;
	const out: Vec2Simple[] = Array.from({length: n}, (_, i)=>{
		const p = closed[i];
		const nrm = ComputeOutwardNormal(closed, i);
		return [p[0] + nrm.x * distance, p[1] + nrm.y * distance] as Vec2Simple;
	});
	return CollapseSelfIntersections(out);
}

/** A minimal self-intersection collapse for an offset ring (mirrors `PathTransforms_Pure.CollapseSelfIntersections`): finds the first non-adjacent edge crossing and bridges the pocket mouth with the crossing point. Returns the ring WITHOUT a closing duplicate. Used in BOTH directions — outward offsets self-intersect at concave corners, inward offsets at convex ones. */
export function CollapseSelfIntersections(offset: Vec2Simple[]): Vec2Simple[] {
	let pts = offset.slice();
	for (let iter = 0; iter < offset.length + 4; iter++) {
		const cross = FindFirstSelfIntersection(pts);
		if (cross == null) break;
		const {i, j, point} = cross;
		const n = pts.length;
		const forwardLen = ArcLength(pts, i, j) + Dist(pts[i], point) + Dist(pts[j], point);
		const wrapLen = ArcLengthWrap(pts, j, i, n) + Dist(pts[j], point) + Dist(pts[i], point);
		if (forwardLen <= wrapLen) {
			pts = [...pts.slice(0, i + 1), point, ...pts.slice(j + 1)];
		} else {
			pts = [...pts.slice(i + 1, j + 1), point];
		}
	}
	return DedupConsecutive(pts);
}

function FindFirstSelfIntersection(pts: Vec2Simple[]): {i: number, j: number, point: Vec2Simple}|n {
	const n = pts.length;
	if (n < 4) return null;
	for (let i = 0; i < n; i++) {
		for (let j = i + 2; j < n; j++) {
			if (i === 0 && j === n - 1) continue; // wrap-adjacent pair
			const a1 = pts[i], a2 = pts[(i + 1) % n];
			const b1 = pts[j], b2 = pts[(j + 1) % n];
			const ip = SegmentIntersection(a1, a2, b1, b2);
			if (ip != null) return {i, j, point: ip};
		}
	}
	return null;
}

function SegmentIntersection(a1: Vec2Simple, a2: Vec2Simple, b1: Vec2Simple, b2: Vec2Simple): Vec2Simple|n {
	const d1x = a2[0] - a1[0], d1y = a2[1] - a1[1];
	const d2x = b2[0] - b1[0], d2y = b2[1] - b1[1];
	const denom = d1x * d2y - d1y * d2x;
	if (Math.abs(denom) < 1e-12) return null;
	const dx = b1[0] - a1[0], dy = b1[1] - a1[1];
	const t = (dx * d2y - dy * d2x) / denom;
	const u = (dx * d1y - dy * d1x) / denom;
	if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
	return [a1[0] + d1x * t, a1[1] + d1y * t] as Vec2Simple;
}

function DedupConsecutive(pts: Vec2Simple[]): Vec2Simple[] {
	if (pts.length < 2) return pts;
	const eps2 = 1e-12;
	const out: Vec2Simple[] = [];
	for (let i = 0; i < pts.length; i++) {
		const prev = out.length ? out[out.length - 1] : pts[pts.length - 1];
		const dx = pts[i][0] - prev[0], dy = pts[i][1] - prev[1];
		if (dx * dx + dy * dy < eps2) continue;
		out.push(pts[i]);
	}
	if (out.length >= 2) {
		const a = out[0], b = out[out.length - 1];
		const dx = a[0] - b[0], dy = a[1] - b[1];
		if (dx * dx + dy * dy < eps2) out.pop();
	}
	return out;
}

function Dist(a: Vec2Simple, b: Vec2Simple): number {
	return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

function ArcLength(pts: Vec2Simple[], i: number, j: number): number {
	let len = 0;
	for (let k = i; k < j; k++) len += Dist(pts[k], pts[k + 1]);
	return len;
}

function ArcLengthWrap(pts: Vec2Simple[], j: number, i: number, n: number): number {
	let len = 0;
	for (let k = j; k < n - 1; k++) len += Dist(pts[k], pts[k + 1]);
	len += Dist(pts[n - 1], pts[0]);
	for (let k = 0; k < i; k++) len += Dist(pts[k], pts[k + 1]);
	return len;
}
